/**
 * Loyalty Points & VIP System
 * - Configurable from Admin Settings
 * - Auto-upgrade to VIP based on totalSpent
 * - Manual upgrade using points
 */

import { doc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'

// Default config (fallback if database is not available)
const DEFAULT_CONFIG = {
  vipThreshold: 200000000, // 200 triệu VNĐ
  vipPointsRatio: 10000, // 10,000 VNĐ = 1 point
  regularPointsRatio: 100000, // 100,000 VNĐ = 1 point
  vipRedeemValue: 1000, // 1 point = 1,000 VNĐ
  regularRedeemValue: 100, // 1 point = 100 VNĐ
  upgradeVIPCost: 2000 // 2,000 points to upgrade to VIP
}

// In-memory cache for settings
let cachedConfig = null
let lastFetchTime = null
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Load loyalty configuration from database
 * Uses caching to avoid excessive reads
 * @returns {Promise<object>} Configuration object
 */
export async function loadLoyaltyConfig() {
  try {
    // Return cached config if still valid
    const now = Date.now()
    if (cachedConfig && lastFetchTime && (now - lastFetchTime < CACHE_DURATION)) {
      return cachedConfig
    }

    // Fetch from database
    const settingsRef = doc(db, 'SETTINGS', 'loyalty')
    const settingsSnap = await getDoc(settingsRef)
    
    if (settingsSnap.exists()) {
      cachedConfig = settingsSnap.data()
      lastFetchTime = now
      return cachedConfig
    }
    
    // Fallback to default
    cachedConfig = DEFAULT_CONFIG
    lastFetchTime = now
    return DEFAULT_CONFIG
  } catch (error) {
    console.warn('Failed to load loyalty config, using default:', error)
    return DEFAULT_CONFIG
  }
}

/**
 * Get current loyalty config (sync)
 * Returns cached config or default
 */
export function getLoyaltyConfig() {
  return cachedConfig || DEFAULT_CONFIG
}

/**
 * Clear config cache (useful after admin updates settings)
 */
export function clearConfigCache() {
  cachedConfig = null
  lastFetchTime = null
}

// Legacy constant for backward compatibility
export const LOYALTY_CONFIG = {
  get VIP_THRESHOLD() { return getLoyaltyConfig().vipThreshold },
  get VIP_POINTS_RATIO() { return getLoyaltyConfig().vipPointsRatio },
  get REGULAR_POINTS_RATIO() { return getLoyaltyConfig().regularPointsRatio },
  get VIP_REDEEM_VALUE() { return getLoyaltyConfig().vipRedeemValue },
  get REGULAR_REDEEM_VALUE() { return getLoyaltyConfig().regularRedeemValue },
  get UPGRADE_VIP_COST() { return getLoyaltyConfig().upgradeVIPCost },
  MEMBERSHIP_TYPES: {
    REGULAR: 'regular',
    VIP: 'vip',
    BLOCKED: 'blocked'
  }
}

/**
 * Calculate points earned from spending
 * @param {number} amountSpent - Amount in VNĐ
 * @param {string} membershipType - 'vip' or 'regular'
 * @param {object} config - Optional config override
 * @returns {number} Points earned (integer, no decimals)
 */
export function calculatePointsEarned(amountSpent, membershipType = 'regular', config = null) {
  if (!amountSpent || amountSpent <= 0) return 0
  
  const cfg = config || getLoyaltyConfig()
  const ratio = membershipType === 'vip' 
    ? cfg.vipPointsRatio 
    : cfg.regularPointsRatio
  
  // Floor to remove decimal places (e.g., 1,073,000 VNĐ / 10,000 = 107.3 → 107 points)
  return Math.floor(amountSpent / ratio)
}

/**
 * Calculate monetary value of points to redeem
 * @param {number} points - Number of points to redeem
 * @param {string} membershipType - 'vip' or 'regular'
 * @param {object} config - Optional config override
 * @returns {number} Value in VNĐ
 */
export function calculatePointsValue(points, membershipType = 'regular', config = null) {
  if (!points || points <= 0) return 0
  
  const cfg = config || getLoyaltyConfig()
  const value = membershipType === 'vip'
    ? cfg.vipRedeemValue
    : cfg.regularRedeemValue
  
  return points * value
}

/**
 * Check if customer is eligible for VIP status based on totalSpent
 * @param {number} totalSpent - Total amount spent in VNĐ
 * @param {object} config - Optional config override
 * @returns {boolean} True if eligible for VIP
 */
export function isEligibleForVIP(totalSpent, config = null) {
  const cfg = config || getLoyaltyConfig()
  return totalSpent >= cfg.vipThreshold
}

/**
 * Check if customer can upgrade to VIP using points
 * @param {number} currentPoints - Current points balance
 * @param {object} config - Optional config override
 * @returns {boolean} True if can upgrade
 */
export function canUpgradeToVIP(currentPoints, config = null) {
  const cfg = config || getLoyaltyConfig()
  return currentPoints >= cfg.upgradeVIPCost
}

/**
 * Validate points redemption
 * @param {number} pointsToRedeem - Points customer wants to redeem
 * @param {number} currentPoints - Customer's current points balance
 * @param {number} orderTotal - Total order amount
 * @param {string} membershipType - 'vip' or 'regular'
 * @param {object} config - Optional config override
 * @returns {object} Validation result
 */
export function validatePointsRedemption(pointsToRedeem, currentPoints, orderTotal, membershipType = 'regular', config = null) {
  const cfg = config || getLoyaltyConfig()
  // Must be positive integer
  if (!Number.isInteger(pointsToRedeem) || pointsToRedeem <= 0) {
    return {
      valid: false,
      error: 'Số điểm phải là số nguyên dương'
    }
  }
  
  // Cannot exceed current balance
  if (pointsToRedeem > currentPoints) {
    return {
      valid: false,
      error: `Không đủ điểm. Hiện có: ${currentPoints.toLocaleString('vi-VN')} điểm`
    }
  }
  
  // Calculate discount value
  const discountValue = calculatePointsValue(pointsToRedeem, membershipType, cfg)
  
  // Cannot exceed order total
  if (discountValue > orderTotal) {
    const maxPoints = Math.floor(orderTotal / (membershipType === 'vip' ? cfg.vipRedeemValue : cfg.regularRedeemValue))
    return {
      valid: false,
      error: `Giá trị điểm vượt quá tổng đơn hàng. Tối đa: ${maxPoints.toLocaleString('vi-VN')} điểm`
    }
  }
  
  return {
    valid: true,
    discountValue,
    finalAmount: orderTotal - discountValue
  }
}

/**
 * Format currency VNĐ
 * @param {number} amount - Amount in VNĐ
 * @returns {string} Formatted string
 */
export function formatCurrency(amount) {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND'
  }).format(amount)
}

/**
 * Format points with commas
 * @param {number} points - Number of points
 * @returns {string} Formatted string
 */
export function formatPoints(points) {
  return new Intl.NumberFormat('vi-VN').format(points)
}

/**
 * Generate secure hash for points validation (simple version)
 * In production, use crypto library or Firebase Cloud Functions
 * @param {string} userId - User ID
 * @param {number} points - Points amount
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Hash string
 */
export function generatePointsHash(userId, points, timestamp) {
  // This is a simple hash - in production use crypto.subtle or server-side
  const str = `${userId}:${points}:${timestamp}:DELYTOUR_SECRET_KEY`
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36)
}

/**
 * Validate points hash
 * @param {string} userId
 * @param {number} points
 * @param {string} timestamp
 * @param {string} providedHash
 * @returns {boolean}
 */
export function validatePointsHash(userId, points, timestamp, providedHash) {
  const expectedHash = generatePointsHash(userId, points, timestamp)
  return expectedHash === providedHash
}

